import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useParticipante } from '../../../contexts/ParticipanteContext';
import { ChatbotAvatar } from '../../chatbot/ChatbotAvatar';
import { useAuth } from '../../../hooks/useAuth';
import {
  useTicketStatusPolling,
  getTicketStatusInfo,
  type TicketNotificationPayload,
} from '@/hooks/useTicketNotifications';
import BannerPesquisaOpiniao from '../BannerPesquisaOpiniao';
import { AccessibilityPanel, AccessibilityButton } from '@/components/base/AccessibilityPanel';
import { useAccessibility } from '@/hooks/useAccessibility';

interface ParticipanteLayoutProps {
  children?: React.ReactNode;
}

// Interface para Notificações
interface Notificacao {
  id: string;
  tipo: 'premiacao' | 'missao' | 'alerta' | 'mensagem' | 'suporte';
  titulo: string;
  descricao: string;
  timestamp: string;
  lida: boolean;
  icone: string;
  cor: string;
}

export function ParticipanteLayout({ children }: ParticipanteLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { participante, campanhas, campanhaAtual, setCampanhaAtual } = useParticipante();
  const { logout } = useAuth();
  
  // Obter progressão da campanha atual
  const progressao = campanhaAtual?.progressao;
  
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCampanhaSelector, setShowCampanhaSelector] = useState(false);

  // Massa de dados de notificações relacionadas à campanha atual
  const [notificacoes, setNotificacoes] = useState<Notificacao[]>([
    // PREMIAÇÕES RECEBIDAS
    {
      id: 'not-1',
      tipo: 'premiacao',
      titulo: '🎉 Parabéns! Você ganhou um iPhone 15 Pro!',
      descricao: 'Você foi sorteado na Roleta da Sorte e ganhou um iPhone 15 Pro 256GB. Acesse a área de recompensas para confirmar o resgate.',
      timestamp: '5 minutos atrás',
      lida: false,
      icone: 'ri-gift-fill',
      cor: 'text-green-600'
    },
    {
      id: 'not-2',
      tipo: 'premiacao',
      titulo: '💰 Vale-Compras de R$ 500 disponível!',
      descricao: 'Você atingiu sua meta mensal e ganhou um vale-compras de R$ 500. Resgate agora na área de recompensas.',
      timestamp: '2 horas atrás',
      lida: false,
      icone: 'ri-shopping-bag-fill',
      cor: 'text-green-600'
    },
    {
      id: 'not-3',
      tipo: 'premiacao',
      titulo: '⭐ Bônus de 250 pontos creditados!',
      descricao: 'Você recebeu 250 pontos de bônus por completar 5 missões consecutivas. Continue assim!',
      timestamp: '1 dia atrás',
      lida: true,
      icone: 'ri-coin-fill',
      cor: 'text-green-600'
    },
    {
      id: 'not-4',
      tipo: 'premiacao',
      titulo: '🎧 AirPods Pro disponível para resgate',
      descricao: 'Você acumulou pontos suficientes! Um AirPods Pro está disponível para resgate no catálogo.',
      timestamp: '2 dias atrás',
      lida: true,
      icone: 'ri-headphone-fill',
      cor: 'text-green-600'
    },

    // MISSÕES CONCLUÍDAS
    {
      id: 'not-5',
      tipo: 'missao',
      titulo: '✅ Missão "Meta Diária de Vendas" concluída!',
      descricao: 'Você completou a missão e ganhou +120 XP. Continue assim para subir de nível!',
      timestamp: '30 minutos atrás',
      lida: false,
      icone: 'ri-flag-fill',
      cor: 'text-blue-600'
    },
    {
      id: 'not-6',
      tipo: 'missao',
      titulo: '🏆 Missão "Conquistador Semanal" concluída!',
      descricao: 'Parabéns! Você completou todas as metas da semana e ganhou +300 XP e um distintivo especial.',
      timestamp: '3 horas atrás',
      lida: false,
      icone: 'ri-trophy-fill',
      cor: 'text-blue-600'
    },
    {
      id: 'not-7',
      tipo: 'missao',
      titulo: '⭐ Missão "Atendimentos de Qualidade" concluída!',
      descricao: 'Você realizou 20 atendimentos com nota 5 estrelas. Ganhou +100 XP e 50 pontos de bônus.',
      timestamp: '1 dia atrás',
      lida: true,
      icone: 'ri-star-fill',
      cor: 'text-blue-600'
    },
    {
      id: 'not-8',
      tipo: 'missao',
      titulo: '🎯 Missão "Mestre do Mês" concluída!',
      descricao: 'Você foi o melhor vendedor do mês! Ganhou +800 XP, 500 pontos e um prêmio especial.',
      timestamp: '3 dias atrás',
      lida: true,
      icone: 'ri-medal-fill',
      cor: 'text-blue-600'
    },

    // ALERTAS DO SISTEMA
    {
      id: 'not-9',
      tipo: 'alerta',
      titulo: '⏰ Missão expirando em breve!',
      descricao: 'A missão "Vendas de Fim de Semana" expira em 2 horas. Complete agora para não perder os pontos!',
      timestamp: '1 hora atrás',
      lida: false,
      icone: 'ri-alarm-warning-fill',
      cor: 'text-orange-600'
    },
    {
      id: 'not-10',
      tipo: 'alerta',
      titulo: '📋 Você tem missões pendentes',
      descricao: 'Existem 3 missões disponíveis que você ainda não iniciou. Acesse a área de missões para começar.',
      timestamp: '4 horas atrás',
      lida: true,
      icone: 'ri-file-list-fill',
      cor: 'text-orange-600'
    },
    {
      id: 'not-11',
      tipo: 'alerta',
      titulo: '🎮 Você está próximo do próximo nível!',
      descricao: 'Faltam apenas 150 XP para você alcançar o nível Ouro. Continue completando missões!',
      timestamp: '1 dia atrás',
      lida: true,
      icone: 'ri-bar-chart-fill',
      cor: 'text-orange-600'
    },
    {
      id: 'not-12',
      tipo: 'alerta',
      titulo: '🎲 Novo jogo disponível!',
      descricao: 'O jogo "Caça ao Tesouro" foi liberado! Jogue agora e ganhe até 150 XP.',
      timestamp: '2 dias atrás',
      lida: true,
      icone: 'ri-gamepad-fill',
      cor: 'text-orange-600'
    },
    {
      id: 'not-13',
      tipo: 'alerta',
      titulo: '⚠️ Pontos expirando em 15 dias',
      descricao: 'Você tem 500 pontos que expiram em 15 dias. Resgate suas recompensas antes que seja tarde!',
      timestamp: '3 dias atrás',
      lida: true,
      icone: 'ri-time-fill',
      cor: 'text-orange-600'
    },

    // MENSAGENS DA PATROCINADORA
    {
      id: 'not-14',
      tipo: 'mensagem',
      titulo: '📢 Mensagem da Diretoria',
      descricao: 'Parabéns pelo excelente desempenho neste trimestre! Vocês são incríveis. Continue assim!',
      timestamp: '6 horas atrás',
      lida: false,
      icone: 'ri-message-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-15',
      tipo: 'mensagem',
      titulo: '🎯 Nova campanha iniciada!',
      descricao: 'A campanha "Vendas de Verão 2024" foi iniciada! Confira as novas metas e prêmios disponíveis.',
      timestamp: '1 dia atrás',
      lida: true,
      icone: 'ri-megaphone-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-16',
      tipo: 'mensagem',
      titulo: '📅 Reunião agendada',
      descricao: 'Reunião de alinhamento de metas agendada para sexta-feira às 14h. Presença obrigatória.',
      timestamp: '1 dia atrás',
      lida: true,
      icone: 'ri-calendar-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-17',
      tipo: 'mensagem',
      titulo: '🌟 Você é destaque da semana!',
      descricao: 'Seu desempenho foi excepcional! Você foi escolhido como destaque da semana. Parabéns!',
      timestamp: '2 dias atrás',
      lida: true,
      icone: 'ri-star-smile-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-18',
      tipo: 'mensagem',
      titulo: '📊 Meta trimestral atualizada',
      descricao: 'As metas do trimestre foram atualizadas. Acesse o painel para ver os novos objetivos.',
      timestamp: '3 dias atrás',
      lida: true,
      icone: 'ri-line-chart-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-19',
      tipo: 'mensagem',
      titulo: '📚 Novo treinamento disponível',
      descricao: 'O treinamento "Técnicas de Vendas Avançadas" está disponível. Complete e ganhe 200 XP.',
      timestamp: '4 dias atrás',
      lida: true,
      icone: 'ri-book-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-20',
      tipo: 'mensagem',
      titulo: '🎊 Evento de premiação confirmado',
      descricao: 'O evento de premiação será no dia 30/06 às 19h no auditório principal. Confirme sua presença.',
      timestamp: '5 dias atrás',
      lida: true,
      icone: 'ri-calendar-event-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-21',
      tipo: 'mensagem',
      titulo: '💡 Dica do dia',
      descricao: 'Sabia que você pode ganhar XP extra jogando os jogos diários? Acesse a área de jogos!',
      timestamp: '5 dias atrás',
      lida: true,
      icone: 'ri-lightbulb-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-22',
      tipo: 'mensagem',
      titulo: '🏅 Ranking atualizado',
      descricao: 'O ranking da campanha foi atualizado. Você está em 3º lugar! Continue assim para chegar ao topo.',
      timestamp: '6 dias atrás',
      lida: true,
      icone: 'ri-trophy-fill',
      cor: 'text-purple-600'
    },
    {
      id: 'not-23',
      tipo: 'mensagem',
      titulo: '🎁 Novos prêmios no catálogo',
      descricao: 'Adicionamos novos prêmios incríveis ao catálogo! Confira agora na área de recompensas.',
      timestamp: '6 dias atrás',
      lida: true,
      icone: 'ri-gift-2-fill',
      cor: 'text-purple-600'
    }
  ]);

  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const handleLogout = async () => {
    setShowProfileMenu(false);
    setIsLoggingOut(true);
    await logout();
    setIsLoggingOut(false);
    navigate('/premiado/login', { replace: true });
  };

  const menuItems = [
    { id: 'inicio', label: 'Início', icon: 'ri-home-line', path: '/premiado/home' },
    { id: 'campanhas', label: 'Campanhas', icon: 'ri-megaphone-line', path: '/premiado/campanhas' },
    { id: 'missoes', label: 'Missões', icon: 'ri-flag-line', path: '/premiado/missoes' },
    { id: 'niveis', label: 'Níveis', icon: 'ri-bar-chart-line', path: '/premiado/niveis' },
    { id: 'distintivos', label: 'Distintivos', icon: 'ri-medal-line', path: '/premiado/distintivos' },
    { id: 'jogos', label: 'Jogos', icon: 'ri-gamepad-line', path: '/premiado/jogos' },
    { id: 'treinamento', label: 'Treinamento', icon: 'ri-graduation-cap-line', path: '/premiado/treinamento' },
    { id: 'social', label: 'Social', icon: 'ri-group-line', path: '/premiado/social' },
    { id: 'recompensas', label: 'Recompensas', icon: 'ri-gift-line', path: '/premiado/recompensas', highlighted: true },
    { id: 'extrato', label: 'Relatórios', icon: 'ri-file-list-line', path: '/premiado/extrato' },
    { id: 'comprovantes', label: 'Comprovantes', icon: 'ri-file-paper-line', path: '/premiado/comprovantes' },
  ];

  const suporteItem = { id: 'suporte', label: 'Suporte', icon: 'ri-customer-service-line', path: '/premiado/suporte' };

  const isActive = (path: string) => location.pathname === path;

  // Calcular notificações não lidas
  const notificacoesNaoLidas = notificacoes.filter(n => !n.lida).length;

  // Notificações de suporte não lidas (para badge no item Suporte)
  const suporteNaoLidas = notificacoes.filter(n => n.tipo === 'suporte' && !n.lida).length;

  // Handler para trocar de campanha
  const handleCampanhaChange = (campanhaId: string) => {
    const novaCampanha = campanhas.find(c => c.id === campanhaId);
    if (novaCampanha) {
      setCampanhaAtual(novaCampanha);
      setShowCampanhaSelector(false);
    }
  };

  // Função para obter progressão de uma campanha específica - AGORA USA DADOS REAIS
  const getCampanhaProgressao = (campanhaId: string) => {
    const campanha = campanhas.find(c => c.id === campanhaId);
    if (!campanha || !campanha.progressao) return null;
    
    return {
      xp: campanha.progressao.xpAtual,
      ranking: campanha.progressao.ranking,
      total: campanha.progressao.totalParticipantes,
      nivel: campanha.progressao.nivelAtual.nome
    };
  };

  // Função para obter informações visuais do nível
  const getNivelInfo = (nivel: string) => {
    const niveis: Record<string, { icon: string; bgColor: string; textColor: string }> = {
      'Bronze': { icon: '🥉', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
      'Prata': { icon: '🥈', bgColor: 'bg-slate-100', textColor: 'text-slate-700' },
      'Ouro': { icon: '🥇', bgColor: 'bg-yellow-100', textColor: 'text-yellow-700' },
      'Platina': { icon: '💎', bgColor: 'bg-cyan-100', textColor: 'text-cyan-700' },
      'Diamante': { icon: '💠', bgColor: 'bg-purple-100', textColor: 'text-purple-700' }
    };
    return niveis[nivel] || niveis['Bronze'];
  };

  // Função para obter badge do tipo de notificação
  const getTipoBadge = (tipo: string) => {
    const badges: Record<string, { label: string; bg: string; text: string }> = {
      'premiacao': { label: 'Premiação', bg: 'bg-green-100', text: 'text-green-700' },
      'missao': { label: 'Missão', bg: 'bg-blue-100', text: 'text-blue-700' },
      'alerta': { label: 'Alerta', bg: 'bg-orange-100', text: 'text-orange-700' },
      'mensagem': { label: 'Mensagem', bg: 'bg-purple-100', text: 'text-purple-700' },
      'suporte': { label: 'Suporte', bg: 'bg-rose-100', text: 'text-rose-700' },
    };
    return badges[tipo] || badges['mensagem'];
  };

  // Handler para notificações de chamados de suporte
  const handleTicketUpdate = useCallback((payload: TicketNotificationPayload) => {
    const { label, icon } = getTicketStatusInfo(payload.novoStatus);

    const statusEmoji: Record<string, string> = {
      'em-andamento': '🔄',
      aguardando: '💬',
      resolvido: '✅',
      fechado: '🔒',
      aberto: '🎫',
    };

    const emoji = statusEmoji[payload.novoStatus] ?? '🎫';

    const novaNotificacao: Notificacao = {
      id: `ticket-${payload.ticketNumber}-${Date.now()}`,
      tipo: 'suporte',
      titulo: `${emoji} Chamado ${payload.ticketNumber} — ${label}`,
      descricao: payload.mensagemSuporte
        ? `Suporte: "${payload.mensagemSuporte}"`
        : `Seu chamado "${payload.subject}" foi atualizado para ${label}.`,
      timestamp: 'Agora',
      lida: false,
      icone: icon,
      cor: 'text-rose-600',
    };

    setNotificacoes(prev => [novaNotificacao, ...prev]);
  }, []);

  // Polling simulado de atualizações de chamados
  useTicketStatusPolling(handleTicketUpdate, true);

  // Escuta evento global disparado pelo TicketModal ao criar chamado
  useEffect(() => {
    const handleTicketCriado = (e: Event) => {
      const { ticketNumber, subject } = (e as CustomEvent).detail as TicketNotificationPayload;
      const novaNotificacao: Notificacao = {
        id: `ticket-criado-${ticketNumber}-${Date.now()}`,
        tipo: 'suporte',
        titulo: `🎫 Chamado ${ticketNumber} aberto`,
        descricao: `Seu chamado "${subject}" foi registrado. Nossa equipe entrará em contato em breve.`,
        timestamp: 'Agora',
        lida: false,
        icone: 'ri-ticket-line',
        cor: 'text-rose-600',
      };
      setNotificacoes(prev => [novaNotificacao, ...prev]);
    };

    window.addEventListener('ticketAtualizado', handleTicketCriado);
    return () => window.removeEventListener('ticketAtualizado', handleTicketCriado);
  }, []);

  // Função para marcar notificação como lida
  const marcarComoLida = (id: string) => {
    setNotificacoes(prev => prev.map(n => n.id === id ? { ...n, lida: true } : n));
  };

  // Função para marcar todas como lidas
  const marcarTodasComoLidas = () => {
    setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
  };

  // Função para excluir notificação
  const excluirNotificacao = (id: string) => {
    setNotificacoes(prev => prev.filter(n => n.id !== id));
  };

  const { isOpen: accessibilityOpen, setIsOpen: setAccessibilityOpen } = useAccessibility();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-50">
      {/* Sidebar Desktop - Hidden on Mobile */}
      <aside className={`hidden lg:block fixed left-0 top-0 h-screen bg-white border-r border-slate-200 shadow-lg transition-all duration-300 z-40 ${
        isSidebarCollapsed ? 'w-20' : 'w-64'
      }`}>
        {/* Logo */}
        <div className={`h-16 border-b border-slate-200 flex items-center ${isSidebarCollapsed ? 'justify-center px-4' : 'justify-between px-6'}`}>
          {!isSidebarCollapsed && (
            <div className="flex items-center gap-2">
              <img 
                src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png" 
                alt="Get Points" 
                className="h-10 w-auto object-contain"
              />
            </div>
          )}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className={`p-2 hover:bg-slate-100 rounded-lg transition-colors ${isSidebarCollapsed ? 'mx-auto' : ''}`}
          >
            <i className={`ri-${isSidebarCollapsed ? 'menu-unfold' : 'menu-fold'}-line text-slate-600 text-xl`}></i>
          </button>
        </div>

        {/* Menu Items */}
        <nav className="p-4 space-y-1 pb-20">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            const isHighlighted = item.highlighted;
            
            return (
              <Link
                key={item.id}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 group relative ${
                  isHighlighted
                    ? isActive
                      ? 'bg-gradient-to-r from-orange-500 to-red-600 text-white shadow-lg shadow-orange-500/30'
                      : 'bg-gradient-to-r from-orange-400 to-red-500 text-white shadow-md shadow-orange-400/20 hover:shadow-lg hover:shadow-orange-500/30 hover:scale-105'
                    : isActive
                    ? 'bg-orange-50 text-orange-600'
                    : 'text-slate-600 hover:bg-slate-50'
                } ${isSidebarCollapsed ? 'justify-center' : ''}`}
                title={isSidebarCollapsed ? item.label : ''}
              >
                <i className={`${item.icon} text-xl ${isHighlighted && !isActive ? 'animate-pulse' : ''}`}></i>
                {!isSidebarCollapsed && (
                  <span className={`font-medium ${isHighlighted ? 'font-bold' : ''}`}>
                    {item.label}
                  </span>
                )}
                {isHighlighted && !isSidebarCollapsed && (
                  <span className="ml-auto">
                    <i className="ri-star-fill text-yellow-300 text-sm animate-pulse"></i>
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Suporte - Rodapé do Sidebar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-slate-50">
          <Link
            to={suporteItem.path}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 relative ${
              location.pathname === suporteItem.path
                ? 'bg-orange-50 text-orange-600'
                : 'text-slate-600 hover:bg-white'
            } ${isSidebarCollapsed ? 'justify-center' : ''}`}
            title={isSidebarCollapsed ? suporteItem.label : ''}
          >
            {/* Ícone com badge quando collapsed */}
            <div className="relative flex-shrink-0">
              <i className={`${suporteItem.icon} text-xl`}></i>
              {suporteNaoLidas > 0 && isSidebarCollapsed && (
                <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 leading-none">
                  {suporteNaoLidas > 9 ? '9+' : suporteNaoLidas}
                </span>
              )}
            </div>
            {!isSidebarCollapsed && (
              <>
                <span className="font-medium flex-1">{suporteItem.label}</span>
                {suporteNaoLidas > 0 && (
                  <span className="min-w-[20px] h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1.5 leading-none">
                    {suporteNaoLidas > 9 ? '9+' : suporteNaoLidas}
                  </span>
                )}
              </>
            )}
          </Link>
        </div>


      </aside>

      {/* Main Content */}
      <div className={`transition-all duration-300 lg:ml-64 ${isSidebarCollapsed ? 'lg:ml-20' : ''}`}>
        {/* Header Desktop - Hidden on Mobile */}
        <header className="hidden lg:block h-16 bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
          <div className="h-full px-6 flex items-center justify-between">
            {/* Left: Título da Página e Seletor de Campanha */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-lg font-bold text-slate-900">
                  {menuItems.find(item => item.path === location.pathname)?.label || 'iBanX'}
                </h1>
              </div>

              {/* Seletor de Campanha */}
              {campanhaAtual && (
                <div className="relative">
                  <button
                    onClick={() => setShowCampanhaSelector(!showCampanhaSelector)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-all duration-200"
                  >
                    <i className="ri-megaphone-line text-orange-600"></i>
                    <span className="font-semibold text-sm text-slate-900">{campanhaAtual.nome}</span>
                    <span className="flex items-center gap-1 text-xs text-slate-500">
                      <span className={`w-2 h-2 rounded-full ${
                        progressao?.nivelAtual.nome === 'Bronze' ? 'bg-orange-700' :
                        progressao?.nivelAtual.nome === 'Prata' ? 'bg-slate-400' :
                        progressao?.nivelAtual.nome === 'Ouro' ? 'bg-yellow-500' :
                        progressao?.nivelAtual.nome === 'Platina' ? 'bg-cyan-400' :
                        'bg-purple-500'
                      }`}></span>
                      {progressao?.nivelAtual.nome}
                    </span>
                    <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${showCampanhaSelector ? 'rotate-180' : ''}`}></i>
                  </button>

                  {/* Dropdown de Campanhas */}
                  {showCampanhaSelector && (
                    <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 max-h-96 overflow-y-auto">
                      <div className="px-4 py-2 border-b border-slate-200">
                        <h3 className="font-semibold text-sm text-slate-900">Selecione uma Campanha</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {campanhas.filter(c => c.status === 'ativa' || c.status === 'pendente').length} disponíveis
                        </p>
                      </div>
                      <div className="py-1">
                        {campanhas.filter(c => c.status === 'ativa' || c.status === 'pendente').map((campanha) => {
                          const isSelected = campanhaAtual?.id === campanha.id;
                          const progressaoCampanha = getCampanhaProgressao(campanha.id);
                          const nivelInfo = getNivelInfo(progressaoCampanha?.nivel || 'Bronze');
                          return (
                            <button
                              key={campanha.id}
                              onClick={() => handleCampanhaChange(campanha.id)}
                              className={`w-full px-4 py-2.5 hover:bg-slate-50 transition-colors text-left ${
                                isSelected ? 'bg-orange-50 border-l-4 border-orange-500' : ''
                              }`}
                            >
                              <div className="flex items-start gap-2">
                                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                                  <img src={campanha.imagem} alt={campanha.nome} className="w-full h-full object-cover" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-1">
                                    <h4 className={`font-semibold text-sm truncate ${isSelected ? 'text-orange-600' : 'text-slate-900'}`}>
                                      {campanha.nome}
                                    </h4>
                                    {isSelected && <i className="ri-check-line text-orange-500 text-sm flex-shrink-0"></i>}
                                  </div>
                                  {campanha.status === 'pendente' && (
                                    <span className="inline-block px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full mt-0.5">Em breve</span>
                                  )}
                                  {progressaoCampanha && (
                                    <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                      <span><i className="ri-star-fill text-yellow-500"></i> {progressaoCampanha.xp.toLocaleString('pt-BR')} XP</span>
                                      <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium ${nivelInfo.bgColor} ${nivelInfo.textColor}`}>
                                        {nivelInfo.icon} {progressaoCampanha.nivel}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: Saldos, Notificações e Perfil */}
            <div className="flex items-center gap-4">
              {/* Saldos Rápidos */}
              <div className="hidden md:flex items-center gap-4 px-4 py-2 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                    <i className="ri-coin-line text-orange-600"></i>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">Pontos</div>
                    <div className="text-sm font-bold text-slate-900">
                      {campanhaAtual?.progressao?.pontosDisponiveis?.toLocaleString('pt-BR') || '0'}
                    </div>
                  </div>
                </div>
                <div className="w-px h-8 bg-slate-200"></div>
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <i className="ri-star-line text-yellow-600"></i>
                  </div>
                  <div>
                    <div className="text-xs text-slate-600">XP</div>
                    <div className="text-sm font-bold text-slate-900">
                      {campanhaAtual?.progressao?.xpAtual?.toLocaleString('pt-BR') || '0'}
                    </div>
                  </div>
                </div>

              </div>

              {/* Notificações */}
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <i className="ri-notification-line text-xl text-slate-600"></i>
                {notificacoesNaoLidas > 0 && (
                  <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {notificacoesNaoLidas}
                  </span>
                )}
              </button>

              {/* Perfil Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="flex items-center gap-3 p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <img
                    src={participante?.foto || 'https://ui-avatars.com/api/?name=Usuario&background=FF6B35&color=fff'}
                    alt={participante?.nome}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div className="hidden md:block text-left">
                    <div className="text-sm font-semibold text-slate-900">{participante?.nome}</div>
                    <div className="text-xs text-slate-600">{participante?.cargo}</div>
                  </div>
                  <i className="ri-arrow-down-s-line text-slate-600"></i>
                </button>

                {/* Dropdown Menu */}
                {showProfileMenu && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                    <Link
                      to="/premiado/perfil"
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-user-line text-slate-600"></i>
                      <span className="text-sm text-slate-700">Meu Perfil</span>
                    </Link>
                    <Link
                      to="/premiado/configuracoes"
                      className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-settings-line text-slate-600"></i>
                      <span className="text-sm text-slate-700">Configurações</span>
                    </Link>
                    <div className="border-t border-slate-200 my-2"></div>
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="w-full flex items-center gap-3 px-4 py-2 hover:bg-red-50 transition-colors text-left disabled:opacity-50"
                    >
                      <i className="ri-logout-box-line text-red-600"></i>
                      <span className="text-sm text-red-600">{isLoggingOut ? 'Saindo...' : 'Sair'}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Header */}
        <header className="lg:hidden sticky top-0 z-30 bg-white border-b border-slate-200 shadow-sm">
          <div className="px-4 py-3">
            {/* Top Row: Logo, Saldos e Notificações */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <img 
                  src="https://static.readdy.ai/image/22e92c18f0b031d861a5e53bf8e362fb/6356cf72c36f3ef424056fffdf6d6f31.png" 
                  alt="Get Points" 
                  className="h-8 w-auto object-contain"
                />
              </div>

              <div className="flex items-center gap-2">
                {/* Saldos Compactos */}
                <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-1.5">
                  <div className="flex items-center gap-1">
                    <i className="ri-coin-line text-orange-600 text-sm"></i>
                    <span className="text-xs font-bold text-slate-900">
                      {campanhaAtual?.progressao?.pontosDisponiveis?.toLocaleString('pt-BR') || '0'}
                    </span>
                  </div>
                  <div className="w-px h-4 bg-slate-300"></div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-line text-yellow-600 text-sm"></i>
                    <span className="text-xs font-bold text-slate-900">
                      {campanhaAtual?.progressao?.xpAtual?.toLocaleString('pt-BR') || '0'}
                    </span>
                  </div>
                </div>

                {/* Notificações */}
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <i className="ri-notification-line text-xl text-slate-600"></i>
                  {notificacoesNaoLidas > 0 && (
                    <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                      {notificacoesNaoLidas}
                    </span>
                  )}
                </button>

                {/* Perfil */}
                <button
                  onClick={() => setShowProfileMenu(!showProfileMenu)}
                  className="relative"
                >
                  <img
                    src={participante?.foto || 'https://ui-avatars.com/api/?name=Usuario&background=FF6B35&color=fff'}
                    alt={participante?.nome}
                    className="w-8 h-8 rounded-full object-cover border-2 border-orange-500"
                  />
                </button>
              </div>
            </div>

            {/* Bottom Row: Campanha Atual */}
            {campanhaAtual && (
              <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-slate-600 mb-0.5">Campanha Atual</div>
                    <div className="font-semibold text-sm text-slate-900 truncate">
                      {campanhaAtual.nome}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCampanhaSelector(!showCampanhaSelector)}
                    className="ml-2 px-3 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-semibold whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-arrow-left-right-line"></i>
                    Trocar
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="w-full bg-white rounded-full h-2">
                      <div
                        className="bg-gradient-to-r from-orange-500 to-red-600 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${progressao?.progressoNivel || 0}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-xs font-bold text-orange-600 whitespace-nowrap">
                    {progressao?.progressoNivel || 0}%
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Campanha Selector */}
          {showCampanhaSelector && (
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCampanhaSelector(false)}>
              <div 
                className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[80vh] overflow-y-auto pb-20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="sticky top-0 bg-white border-b border-slate-200 px-4 py-4 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-lg text-slate-900">Selecione uma Campanha</h3>
                    <p className="text-xs text-slate-600 mt-1">
                      {campanhas.filter(c => c.status === 'ativa' || c.status === 'pendente').length} campanhas disponíveis
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCampanhaSelector(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-100"
                  >
                    <i className="ri-close-line text-xl text-slate-600"></i>
                  </button>
                </div>
                
                <div className="p-4 space-y-3">
                  {campanhas.filter(c => c.status === 'ativa' || c.status === 'pendente').map((campanha) => {
                    const isSelected = campanhaAtual?.id === campanha.id;
                    const progressaoCampanha = getCampanhaProgressao(campanha.id);
                    const nivelInfo = getNivelInfo(progressaoCampanha?.nivel || 'Bronze');
                    
                    return (
                      <button
                        key={campanha.id}
                        onClick={() => handleCampanhaChange(campanha.id)}
                        className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                          isSelected 
                            ? 'border-orange-500 bg-orange-50' 
                            : 'border-slate-200 bg-white hover:border-orange-300'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100">
                            <img 
                              src={campanha.imagem} 
                              alt={campanha.nome}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-bold text-base ${
                                  isSelected ? 'text-orange-600' : 'text-slate-900'
                                }`}>
                                  {campanha.nome}
                                </h4>
                                {campanha.status === 'pendente' && (
                                  <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">Em breve</span>
                                )}
                              </div>
                              {isSelected && (
                                <i className="ri-checkbox-circle-fill text-orange-500 text-xl flex-shrink-0"></i>
                              )}
                            </div>
                            
                            {progressaoCampanha && (
                              <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span className="text-slate-600 flex items-center gap-1">
                                  <i className="ri-star-fill text-yellow-500"></i>
                                  {progressaoCampanha.xp.toLocaleString('pt-BR')} XP
                                </span>
                                <span className="text-slate-600">
                                  #{progressaoCampanha.ranking} de {progressaoCampanha.total}
                                </span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${nivelInfo.bgColor} ${nivelInfo.textColor}`}>
                                  {nivelInfo.icon} {progressaoCampanha.nivel}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Mobile Profile Menu */}
          {showProfileMenu && (
            <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowProfileMenu(false)}>
              <div 
                className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl pb-20"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-4">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-200">
                    <img
                      src={participante?.foto || 'https://ui-avatars.com/api/?name=Usuario&background=FF6B35&color=fff'}
                      alt={participante?.nome}
                      className="w-16 h-16 rounded-full object-cover border-2 border-orange-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-lg text-slate-900 truncate">{participante?.nome}</div>
                      <div className="text-sm text-slate-600">{participante?.cargo}</div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Link
                      to="/premiado/perfil"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-lg transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-user-line text-slate-600 text-xl"></i>
                      <span className="text-base text-slate-700">Meu Perfil</span>
                    </Link>
                    <Link
                      to="/premiado/configuracoes"
                      className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 rounded-lg transition-colors"
                      onClick={() => setShowProfileMenu(false)}
                    >
                      <i className="ri-settings-line text-slate-600 text-xl"></i>
                      <span className="text-base text-slate-700">Configurações</span>
                    </Link>
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 rounded-lg transition-colors text-left disabled:opacity-50"
                    >
                      <i className="ri-logout-box-line text-red-600 text-xl"></i>
                      <span className="text-base text-red-600">{isLoggingOut ? 'Saindo...' : 'Sair'}</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          {children || <Outlet />}
        </main>
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50">
        <div className="flex items-center justify-around h-16 px-2">
          {/* Início */}
          <button
            onClick={() => navigate('/premiado/home')}
            className={`flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all duration-200 active:scale-95 min-w-[64px] ${
              location.pathname === '/premiado/home'
                ? 'text-orange-600'
                : 'text-slate-500'
            }`}
          >
            <i className={`${location.pathname === '/premiado/home' ? 'ri-home-5-fill' : 'ri-home-5-line'} text-[26px]`}></i>
            <span className={`text-[10px] ${location.pathname === '/premiado/home' ? 'font-semibold' : 'font-medium'}`}>
              Início
            </span>
          </button>

          {/* Recompensas */}
          <button
            onClick={() => navigate('/premiado/recompensas')}
            className={`flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all duration-200 active:scale-95 min-w-[64px] ${
              location.pathname === '/premiado/recompensas'
                ? 'text-orange-600'
                : 'text-slate-500'
            }`}
          >
            <i className={`${location.pathname === '/premiado/recompensas' ? 'ri-gift-fill' : 'ri-gift-line'} text-[26px]`}></i>
            <span className={`text-[10px] ${location.pathname === '/premiado/recompensas' ? 'font-semibold' : 'font-medium'}`}>
              Recompensas
            </span>
          </button>

          {/* Social */}
          <button
            onClick={() => navigate('/premiado/social')}
            className={`flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all duration-200 active:scale-95 min-w-[64px] ${
              location.pathname === '/premiado/social'
                ? 'text-orange-600'
                : 'text-slate-500'
            }`}
          >
            <i className={`${location.pathname === '/premiado/social' ? 'ri-group-fill' : 'ri-group-line'} text-[26px]`}></i>
            <span className={`text-[10px] ${location.pathname === '/premiado/social' ? 'font-semibold' : 'font-medium'}`}>
              Social
            </span>
          </button>

          {/* Missões */}
          <button
            onClick={() => navigate('/premiado/missoes')}
            className={`flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all duration-200 active:scale-95 min-w-[64px] ${
              location.pathname === '/premiado/missoes'
                ? 'text-orange-600'
                : 'text-slate-500'
            }`}
          >
            <i className={`${location.pathname === '/premiado/missoes' ? 'ri-flag-fill' : 'ri-flag-line'} text-[26px]`}></i>
            <span className={`text-[10px] ${location.pathname === '/premiado/missoes' ? 'font-semibold' : 'font-medium'}`}>
              Missões
            </span>
          </button>

          {/* Níveis */}
          <button
            onClick={() => navigate('/premiado/niveis')}
            className={`flex flex-col items-center justify-center gap-0.5 px-4 py-2 rounded-xl transition-all duration-200 active:scale-95 min-w-[64px] ${
              location.pathname === '/premiado/niveis'
                ? 'text-orange-600'
                : 'text-slate-500'
            }`}
          >
            <i className={`${location.pathname === '/premiado/niveis' ? 'ri-bar-chart-fill' : 'ri-bar-chart-line'} text-[26px]`}></i>
            <span className={`text-[10px] ${location.pathname === '/premiado/niveis' ? 'font-semibold' : 'font-medium'}`}>
              Níveis
            </span>
          </button>
        </div>
      </nav>

      {/* Notifications Panel */}
      {showNotifications && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={() => setShowNotifications(false)}>
          <div
            className="absolute right-0 top-0 h-full w-full lg:max-w-lg bg-white shadow-xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-4 lg:p-6 border-b border-slate-200 bg-gradient-to-r from-orange-500 to-red-600">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-bold text-white">Notificações</h3>
                <button
                  onClick={() => setShowNotifications(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <i className="ri-close-line text-xl text-white"></i>
                </button>
              </div>
              {campanhaAtual && (
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <i className="ri-megaphone-line"></i>
                  <span>{campanhaAtual.nome}</span>
                </div>
              )}
              {notificacoesNaoLidas > 0 && (
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-sm text-white/90">
                    {notificacoesNaoLidas} não {notificacoesNaoLidas === 1 ? 'lida' : 'lidas'}
                  </span>
                  <button
                    onClick={marcarTodasComoLidas}
                    className="text-sm text-white hover:text-white/80 underline transition-colors"
                  >
                    Marcar todas como lidas
                  </button>
                </div>
              )}
            </div>

            {/* Lista de Notificações */}
            <div className="flex-1 overflow-y-auto">
              {notificacoes.length === 0 ? (
                <div className="p-8 text-center">
                  <i className="ri-notification-off-line text-6xl text-slate-300 mb-4"></i>
                  <p className="text-sm text-slate-600">
                    Nenhuma notificação no momento
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-slate-200">
                  {notificacoes.map((notificacao) => {
                    const badge = getTipoBadge(notificacao.tipo);
                    
                    return (
                      <div
                        key={notificacao.id}
                        className={`p-4 hover:bg-slate-50 transition-colors ${
                          !notificacao.lida ? 'bg-orange-50/50' : ''
                        }`}
                      >
                        <div className="flex gap-3">
                          {/* Ícone */}
                          <div className={`flex-shrink-0 w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center ${notificacao.cor}`}>
                            <i className={`${notificacao.icone} text-xl`}></i>
                          </div>

                          {/* Conteúdo */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
                                {badge.label}
                              </span>
                              {!notificacao.lida && (
                                <span className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-1"></span>
                              )}
                            </div>
                            
                            <h4 className="font-semibold text-sm text-slate-900 mb-1 line-clamp-2">
                              {notificacao.titulo}
                            </h4>
                            
                            <p className="text-sm text-slate-600 mb-2 line-clamp-2">
                              {notificacao.descricao}
                            </p>
                            
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-slate-500">
                                {notificacao.timestamp}
                              </span>
                              
                              <div className="flex items-center gap-2">
                                {!notificacao.lida && (
                                  <button
                                    onClick={() => marcarComoLida(notificacao.id)}
                                    className="text-xs text-orange-600 hover:text-orange-700 font-medium transition-colors"
                                  >
                                    Marcar como lida
                                  </button>
                                )}
                                <button
                                  onClick={() => excluirNotificacao(notificacao.id)}
                                  className="p-1 hover:bg-slate-200 rounded transition-colors"
                                  title="Excluir notificação"
                                >
                                  <i className="ri-delete-bin-line text-slate-500 text-sm"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Chatbot Avatar - Aparece em todas as páginas */}
      <ChatbotAvatar />

      {/* Banner de Pesquisa de Opinião - Aparece aleatoriamente */}
      <BannerPesquisaOpiniao />
      <AccessibilityButton
        onClick={() => setAccessibilityOpen(!accessibilityOpen)}
        isOpen={accessibilityOpen}
      />
      <AccessibilityPanel
        isOpen={accessibilityOpen}
        onClose={() => setAccessibilityOpen(false)}
      />
    </div>
  );
}